/**
 * Sprint P.0 · Página 404 pública (elimina el soft 404 anterior).
 *
 * Sustituye el `<Route path="*" element={<Navigate to="/" replace />} />`
 * heredado, que devolvía 302→200 al home y era clasificado como "soft 404"
 * por Google Search Console.
 *
 * Comportamiento:
 *   · Añade `<meta name="robots" content="noindex, nofollow">` para no indexar
 *   · Muestra opciones útiles (marketplace, home, buscar centro) en lugar de
 *     redirigir al home ciegamente.
 *   · No es una redirección: la URL rota permanece en la barra para que el
 *     usuario entienda que la página no existe.
 */
import { Link } from "react-router-dom";
import { useEffect } from "react";
import GlobalHeader from "../components/layout/GlobalHeader";
import { MagnifyingGlass, Storefront, House, ArrowLeft } from "@phosphor-icons/react";

export default function NotFound() {
  useEffect(() => {
    document.title = "Página no encontrada · AutoGlow Ecosystem";
    // Marcar la página como noindex (para GSC "URL not found")
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute("content") || "";
    meta.setAttribute("content", "noindex, nofollow");
    return () => {
      // Al desmontar dejamos que la siguiente ruta configure su propio robots
      meta.setAttribute("content", prev || "index, follow");
    };
  }, []);

  return (
    <div className="min-h-screen ag-radial text-[var(--ag-text)] flex flex-col" data-testid="not-found-page">
      <GlobalHeader variant="auto" testId="notfound-header" />
      <div className="flex-1 flex items-center justify-center px-5">
        <div className="text-center max-w-2xl mx-auto py-16 sm:py-24">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--ag-gold)]/30 rounded-full text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]" data-testid="not-found-tag">
            <MagnifyingGlass size={12} weight="fill" /> 404 · Página no encontrada
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]" data-testid="not-found-title">
            No encontramos<br/>la página que buscas.
          </h1>
          <p className="text-base text-[var(--ag-text-dim)] mt-6 max-w-lg mx-auto">
            Puede que el enlace haya cambiado o que el centro ya no esté disponible.
            Prueba con nuestras rutas principales:
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold rounded-md px-6 h-12 text-base hover:shadow-[0_18px_46px_-14px_rgba(255,183,3,0.55)] hover:bg-[#FFCD3C] transition-all active:scale-[0.98]"
              data-testid="not-found-cta-marketplace"
            >
              <Storefront weight="bold" /> Ir al marketplace
            </Link>
            <Link
              to="/landing"
              className="inline-flex items-center gap-2 text-sm font-medium text-[var(--ag-text)] hover:text-[var(--ag-gold)] transition-colors px-3 h-12"
              data-testid="not-found-cta-home"
            >
              <House weight="duotone" /> Volver al inicio
            </Link>
          </div>
          <button
            onClick={() => window.history.back()}
            className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] transition-colors underline-offset-4 hover:underline mt-8 inline-flex items-center gap-1.5"
            data-testid="not-found-cta-back"
          >
            <ArrowLeft size={12} weight="bold" /> Atrás
          </button>
        </div>
      </div>
    </div>
  );
}
